import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { PersonalType } from '../emuns/personal-type';
import { environment } from '../../environments/environment';
import { Ciudadano, ServidorPublico } from '../models/ServidorPublico';
import { Participante, ParticipanteType } from '../models/ParticipanteType';
import { CapitalizePipe } from '../pipes/capitalize.pipe';

@Injectable({
  providedIn: 'root',
})
export class ParticipanteService {
  apiUrl = environment.apiUrl;
  private LIST_SERVIDOR_PUBLICO_URL = environment.apiUrl + "sp/s";
  private TODOS_SERVIDOR_PUBLICO_URL = environment.apiUrl + "sp/t";
  private GET_SERVIDOR_PUBLICO_BY_CIRUC_URL = environment.apiUrl + "sp/";
  private GET_SERVIDORES_POR_DEPARTAMENTO_URL = environment.apiUrl + "sp/d/"
  private POST_CIUDADANO_URL = environment.apiUrl + "ciu/"

  constructor(private http: HttpClient) { }  

  getExternalCiudadano(ciruc: String):Observable<Ciudadano>{
    return this.http.get<Ciudadano>(this.POST_CIUDADANO_URL + ciruc)
  }

  postCiudadano(ciudadano: Ciudadano): Observable<Ciudadano> {
    return this.http.post<Ciudadano>(this.POST_CIUDADANO_URL + ciudadano.ciruc, ciudadano)
  }

  getParticipanteFromCiudadano(ciudadano: Ciudadano): Participante {
    const participante: Participante = {
      id: ciudadano.id,
      ciruc: ciudadano.ciruc,
      nombres: new CapitalizePipe().transform((ciudadano.nombres || '').trim() + " " + (ciudadano.apellidos || '').trim()),
      cargo: ciudadano.cargo || '',
      institucion: '',
      titulo: ciudadano.tratamientoTitulo,
      idTipoSolicitante: 2,
      departamento: '',
      portavoz: 0
    };
    return participante
  }

  getCiudadano(filter: String): Observable<Ciudadano[]> {
    return this.http.get<Ciudadano[]>(this.apiUrl + 'ciu/s/' + filter);
  }

  getParticipanteCiudadano(filter: String): Observable<Participante[]> {
    return this.getCiudadano(filter).pipe(
      map((response: Ciudadano[]) => {
        let data: Participante[] = [];
        response.forEach((res: Ciudadano) => {
          const participante: Participante = {
            id: res.id,
            ciruc: res.ciruc,
            nombres: new CapitalizePipe().transform((res.nombres || '').trim() + " " + (res.apellidos || '').trim()),
            cargo: res.cargo || '',
            institucion: '',
            titulo: res.tratamientoTitulo,
            idTipoSolicitante: 2,
            departamento: '',
            portavoz: 0
          };
          data.push(participante);
        });
        return data;
      })
    );
  }

  getServidorPublico(qParams?: String): Observable<ServidorPublico[]> {
    return this.http.get<ServidorPublico[]>(this.LIST_SERVIDOR_PUBLICO_URL+(qParams ? "?q=" + qParams : ""));
  }

  todosSp(): Observable<ServidorPublico[]> {
    return this.http.get<ServidorPublico[]>(this.TODOS_SERVIDOR_PUBLICO_URL);
  }

  getServidorPublicoByCiruc(ciruc: string): Observable<ServidorPublico> {
    return this.http.get<ServidorPublico>(this.GET_SERVIDOR_PUBLICO_BY_CIRUC_URL + ciruc);
  }

  getNombreServidorPublicoByCiruc(ciruc: string): Observable<string> {
    return this.getServidorPublicoByCiruc(ciruc).pipe(
      map(servidorPublico => `${servidorPublico.nombres.trim()} ${servidorPublico.apellidos.trim()}`)
    );
  }

  getParticipanteServidorPublico(qParams?: String): Observable<any[]> {
    return this.getServidorPublico(qParams).pipe(
      map((response: any[]) => {
        let data: Participante[] = [];
        response.forEach((res: ServidorPublico) => {
          const participante: any = {
            id: res.id.toString(),
            ciruc: res.usuario||'',
            nombres: new CapitalizePipe().transform((res.nombres || '').trim() + " " + (res.apellidos || '').trim()),
            cargo: res.cargo || '',
            institucion: '',
            titulo: res.titulo || '',
            idTipoSolicitante: 1,
            departamento: res.departamento||'',
            fechaUso: res.fechaUso||'',
            portavoz: 0
          };
          data.push(participante);
        });
        return data;
      })
    );
  }

  generateParticipanteType(data: ServidorPublico[]): any[] {
    let dataSource: any[] = [];
    data.forEach((servidor) => {
      const nombres = new CapitalizePipe().transform(servidor.nombres || '');
      const apellidos = new CapitalizePipe().transform(servidor.apellidos || '');
      const participante: any = {
        id: servidor.id,
        titulo: servidor.titulo,
        ciruc: servidor.usuario,
        nombre: nombres,
        apellido: apellidos,
        correo: servidor.correo,
        cargo: servidor.cargo,
        departamento: servidor.departamento,
        fechaUso: servidor.fechaUso,
        portavoz: 0
      };
      dataSource.push(participante);
    });
    return dataSource;
  }

  getListasEnvio(): Observable<any[]> {
    return this.http.get<any>(this.apiUrl + 'a/u/le').pipe(
      map((response: any) => {
        if (!response) return [];
        if (Array.isArray(response)) return response;
        if (response.data && Array.isArray(response.data)) return response.data;
        if (response.listas && Array.isArray(response.listas)) return response.listas;
        // if single object, wrap it
        if (typeof response === 'object') return [response];
        return [];
      }),
      catchError((err: HttpErrorResponse) => {
        const status = err?.status ?? 0;
        const isAuthError = status === 401 || status === 403;

        if (isAuthError) {
          throw err;
        }

        return of([]);
      })
    );
  }
  getParticipanteListaEnvio(id: any): Observable<any[]> {
    // use correct backend endpoint: /a/u/le/{id}/destinatarios
    return this.http.get<any[]>(this.apiUrl + 'a/u/le/' + id + '/destinatarios').pipe(
      map((response: any) => {
        let data: any[] = [];
        (response || []).forEach((res: any) => {
          const participante: Participante = {
            id: res.id ? res.id.toString() : res.id,
            ciruc: res.usuario || '',
            nombres: new CapitalizePipe().transform(((res.apellidos || '').trim() + ' ' + (res.nombres || '').trim()).trim()),
            cargo: res.cargo || '',
            institucion: '',
            titulo: res.titulo || '',
            idTipoSolicitante: res.idTipoSolicitante != null ? res.idTipoSolicitante : 1,
            departamento: res.departamento || '',
            portavoz: 0
          };
          data.push(participante);
        });
        return data;
      })
    );
  }

  createListaEnvio(lista: { nombre: string, codigosDestinatarios: number[] }): Observable<any> {
    return this.http.post<any>(this.apiUrl + 'a/u/le', lista);
  }

  editListaEnvio(id: number, lista: { nombre: string, codigosDestinatarios: number[] }): Observable<any> {
    return this.http.put<any>(this.apiUrl + 'a/u/le/' + id, lista);
  }

  deleteListaEnvio(id: number): Observable<any> {
    // backend returns text/plain for delete; request as text to avoid JSON parse errors
    return this.http.delete(this.apiUrl + 'a/u/le/' + id, { responseType: 'text' as 'json' });
  }

  getServidoresPorDepartamento(idDepartamento: string): Observable<ServidorPublico[]> {
    return this.http.get<ServidorPublico[]>(this.GET_SERVIDORES_POR_DEPARTAMENTO_URL + idDepartamento)
  }

  getServidoresParaLista(qParams?: string): Observable<any[]> {
    // backend endpoint: /a/u/le/sp?q=...
    const url = this.apiUrl + 'a/u/le/sp' + (qParams ? ('?q=' + encodeURIComponent(qParams)) : '');
    return this.http.get<any[]>(url).pipe(
      map((response: any[]) => {
        const data: any[] = [];
        (response || []).forEach((res: any) => {
          data.push({
            id: res.id ? res.id.toString() : res.id,
            ciruc: res.usuario || '',
            nombres: new CapitalizePipe().transform(((res.apellidos || '').trim() + ' ' + (res.nombres || '').trim()).trim()),
            titulo: res.titulo || '',
            idTipoSolicitante: res.idTipoSolicitante != null ? res.idTipoSolicitante : 1,
            cargo: res.cargo || '',
            departamento: res.departamento || ''
          });
        });
        return data;
      })
    );
  }
}
